// components/AttachmentSection/index.tsx
// 添付ファイル一覧＋追加ボタンの汎用セクションコンポーネント
//
// 現在の使用箇所:
//   - src/components/FollowListModal/index.tsx（フォロー詳細モーダル内）
//
// 将来的な差し替え候補:
//   - src/pages/BusinessCardDetailPage.tsx（フォロー追加モーダル内）
//     差し替え時の注意: BusinessCardDetailPage 側はファイルサイズをバイト数(number)で
//     管理しているため、size prop に number を渡せばこのコンポーネント内で自動変換される。

import React from 'react';
import { Button } from 'antd';
import {
  PaperClipOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------

export interface AttachmentFile {
  /** ファイル識別子 */
  id: number | string;
  /** ファイル名 */
  name: string;
  /**
   * ファイルサイズ
   * - string: 表示用フォーマット済み文字列（例: "1.2 MB"）
   * - number: バイト数（コンポーネント内で自動変換）
   */
  size: number | string;
}

export interface AttachmentSectionProps {
  /** 表示するファイル一覧 */
  files: AttachmentFile[];
  /**
   * プレビューボタンクリック時のコールバック
   * TODO: 本実装時は対象ファイルのプレビュー処理を渡すこと
   */
  onPreview: (file: AttachmentFile) => void;
  /**
   * ダウンロードボタンクリック時のコールバック
   * TODO: 本実装時は対象ファイルのダウンロード処理を渡すこと
   */
  onDownload: (file: AttachmentFile) => void;
  /**
   * 削除ボタンクリック時のコールバック
   * 削除確認モーダルの表示など、後続処理は呼び出し元で制御すること
   * TODO: 本実装時は対象ファイルの削除処理を渡すこと
   */
  onDelete: (file: AttachmentFile) => void;
  /**
   * 「ファイルを追加」ボタンクリック時のコールバック
   * ファイル選択モーダルの表示など、呼び出し元で制御すること
   */
  onAdd: () => void;
  /**
   * ファイル一覧全体に外枠ボーダーを表示するか（デフォルト: false）
   * BusinessCardDetailPage 側の見た目に合わせる場合は true にすること
   */
  showBorder?: boolean;
  /**
   * 「ファイルを追加」ボタンを非表示にするか（デフォルト: false）
   * 参照専用で表示したい場合に使用
   */
  readOnly?: boolean;
}

// ----------------------------------------------------------------
// ユーティリティ
// ----------------------------------------------------------------

/** バイト数を表示用文字列に変換する */
const formatSize = (size: number | string): string => {
  if (typeof size === 'string') return size;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
};

// ----------------------------------------------------------------
// コンポーネント
// ----------------------------------------------------------------

const AttachmentSection: React.FC<AttachmentSectionProps> = ({
  files,
  onPreview,
  onDownload,
  onDelete,
  onAdd,
  showBorder = false,
  readOnly = false,
}) => {
  return (
    <div style={{ margin: '0 4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: '#8c8c8c' }}>添付ファイル</span>
        {!readOnly && (
          <Button type="text" size="small" icon={<PaperClipOutlined />} onClick={onAdd}>
            追加
          </Button>
        )}
      </div>

      {files.length > 0 && (
        <div
          style={
            showBorder
              ? { border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }
              : undefined
          }
        >
          {files.map((file, index) => (
            <div
              key={file.id}
              style={{
                padding: '8px',
                marginBottom: index < files.length - 1 ? '8px' : 0,
                borderBottom: index < files.length - 1 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              {/* 1段目: クリップアイコン + ファイル名 + 容量 */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <PaperClipOutlined style={{ marginRight: 8, flexShrink: 0 }} />
                <span style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{file.name}</span>
                <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                  {formatSize(file.size)}
                </span>
              </div>
              {/* 2段目: プレビュー・ダウンロード・削除ボタン */}
              <div style={{ display: 'flex', marginTop: 6, gap: 4 }}>
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  style={{ flex: 1 }}
                  onClick={() => onPreview(file)}
                />
                <Button
                  type="text"
                  icon={<DownloadOutlined style={{ color: '#1677ff' }} />}
                  style={{ flex: 1, backgroundColor: '#e6f4ff' }}
                  onClick={() => onDownload(file)}
                />
                <Button
                  type="text"
                  icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                  style={{ flex: 1, backgroundColor: '#fff0f0' }}
                  onClick={() => onDelete(file)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AttachmentSection;
